import { AxiosInstance, AxiosResponse } from "axios"

/**
 * Shared client half of the server's async-job contract.
 *
 * An endpoint registered as async-capable and called with an `ASync-Request: <secs>`
 * header either finishes inside that window (200, body is the real result) or hands
 * back a job id (202, body is the bare id). From there the client polls
 * `/async-ready?id=` until it returns 200 and then reads `/async-get?id=`, which
 * replays the original response verbatim, content type included.
 *
 * This used to live in `api/Engine/data-reporting.ts` behind two "move somewhere more
 * appropriate" TODOs, back when exports were the only caller. `/data-integrate`,
 * `/base-check` and `/base-check-clean` call it too, so it lives here now; the old modules
 * re-export these names so no existing call site had to change.
 */

const ASYNC_POLL_INTERVAL_MS = 5000
const ASYNC_MAX_WAIT_MS = 20 * 60 * 1000 // 20-minute safety cap

/**
 * Polls the server to check if an asynchronous request is ready.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param asyncID - The identifier for the asynchronous request to check readiness for.
 * @returns The response object from the API call (202 = still running, 200 = ready).
 * @throws Will throw an error if polling fails.
 */
export async function pollAsync(
  axiosInstance: AxiosInstance,
  asyncID: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/async-ready?id=${asyncID}`)
  return response
}

/**
 * Fetches the result of a completed asynchronous request.
 *
 * `responseType: "text"` + an identity `transformResponse` are LOAD-BEARING here, exactly as
 * they are on every async submit — this call is the same body coming back by another door, and
 * it must arrive in the same shape.
 *
 * The servers answer `Content-Type: text/plain` with a body that is often JSON, and axios 1.x
 * default-transforms any string body that parses as JSON into an OBJECT whenever no
 * `responseType` is set (`forcedJSONParsing && !this.responseType`). Without these two options
 * `resolveAsyncBody`'s `String(result.data)` produced the literal string `"[object Object]"`,
 * and `resolveAsyncJson` then threw "returned a response that could not be read".
 *
 * Which is the worst possible moment for a client to give up: a job only reaches this path
 * because it OUTRAN its wait window, so its work has already happened. For `/base-check-clean`
 * that means the data file, the pre-clean document and the audit record are all written by the
 * time this line runs — the user would have been told their clean failed while it had in fact
 * succeeded, and the obvious next move (run it again) would then have answered "nothing to
 * clean" and looked like a second failure.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param asyncID - The identifier for the asynchronous request whose result is being fetched.
 * @returns The response object from the API call containing the result data, as raw text.
 * @throws Will throw an error if fetching the result fails.
 */
export async function fetchAsyncResult(
  axiosInstance: AxiosInstance,
  asyncID: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/async-get?id=${asyncID}`, {
    responseType: "text",
    transformResponse: (data: string) => data,
  })
  return response
}

/**
 * Resolves the raw body of an async-capable request, whether the server finished it
 * synchronously (200) or handed back a job id (202).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param submitResponse - The response from the initial submit request
 * @param label - Used in the timeout message, e.g. "Export" / "Data integration" / "Base check"
 * @returns The trimmed response body as a string
 * @throws If the submit status is neither 200 nor 202, or the wait cap is exceeded
 */
export async function resolveAsyncBody(
  axiosInstance: AxiosInstance,
  submitResponse: AxiosResponse<string>,
  label = "Request"
): Promise<string> {
  // Fast path: the server finished within its synchronous wait window.
  if (submitResponse.status === 200) {
    return String(submitResponse.data).trim()
  }
  if (submitResponse.status !== 202) {
    throw new Error(`Unexpected ${label.toLowerCase()} status ${submitResponse.status}`)
  }

  // Async path: the 202 body is the plain-string job id.
  const asyncID = String(submitResponse.data).trim()
  const started = Date.now()

  for (;;) {
    if (Date.now() - started > ASYNC_MAX_WAIT_MS) {
      throw new Error(`${label} timed out waiting for the server to finish`)
    }
    await new Promise((resolve) => setTimeout(resolve, ASYNC_POLL_INTERVAL_MS))

    const ready = await pollAsync(axiosInstance, asyncID)
    if (ready.status === 200) {
      const result = await fetchAsyncResult(axiosInstance, asyncID)
      return String(result.data).trim()
    }
    // status 202 => still running; keep polling.
  }
}

/**
 * Resolves an async-capable request whose result is JSON.
 *
 * The submit call must be made with `responseType: "text"` and an identity
 * `transformResponse`, otherwise axios parses the 202 body (a bare job id) as JSON
 * and mangles it. Parsing therefore happens here, once, on the final body.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param submitResponse - The response from the initial submit request
 * @param label - Used in the timeout message
 * @returns The parsed JSON payload
 * @throws If the wait cap is exceeded or the final body is not valid JSON
 */
export async function resolveAsyncJson<T>(
  axiosInstance: AxiosInstance,
  submitResponse: AxiosResponse<string>,
  label = "Request"
): Promise<T> {
  const body = await resolveAsyncBody(axiosInstance, submitResponse, label)
  try {
    return JSON.parse(body) as T
  } catch {
    throw new Error(`${label} returned a response that could not be read`)
  }
}

/**
 * Resolves the base64 payload of an export. Thin wrapper kept for the export call
 * sites, which name their result explicitly.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param submitResponse - The response from the initial export submit request
 * @returns The trimmed base64 string of the exported (zipped) file
 */
export async function resolveAsyncBase64(
  axiosInstance: AxiosInstance,
  submitResponse: AxiosResponse<string>
): Promise<string> {
  return resolveAsyncBody(axiosInstance, submitResponse, "Export")
}
